import React, { useState, useEffect, useCallback, useRef } from 'react'
import { personasApi } from '../../services/api'

const FamilyMemberForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    dni: initialData?.dni || '',
    nombre: initialData?.nombre || '',
    apellido: initialData?.apellido || '',
    relacion: initialData?.relacion || 'padre',
    edad: initialData?.edad || '',
    convive: initialData?.convive ?? true,
    observaciones: initialData?.observaciones || ''
  })

  // DNI lookup state
  const [dniStatus, setDniStatus] = useState('idle') // idle | searching | found | not_found | error
  const [foundPerson, setFoundPerson] = useState(null)
  const [fieldsLocked, setFieldsLocked] = useState(false)
  const debounceRef = useRef(null)

  const isEditing = Boolean(initialData)

  // Auto-search when DNI has 7-8 digits
  const searchByDni = useCallback(async (dni) => {
    const clean = String(dni).replace(/\D/g, '')
    if (clean.length < 7) {
      setDniStatus('idle')
      setFoundPerson(null)
      setFieldsLocked(false)
      return
    }

    setDniStatus('searching')
    try {
      const res = await personasApi.search({ dni: clean, limit: 5 })
      const personas = res.data?.data || []
      // Find exact match
      const exact = personas.find(p => String(p.dni).replace(/\D/g, '') === clean)
      if (exact) {
        // Obtenemos los detalles enriquecidos (con IA) usando getById
        try {
            const detailRes = await personasApi.getById(exact.id);
            if (detailRes.data?.data) {
                exact.ai_insights = detailRes.data.data.ai_insights;
            }
        } catch (e) { console.warn('No se pudo obtener AI insights'); }

        setDniStatus('found')
        setFoundPerson(exact)
        setFieldsLocked(true)
        setFormData(prev => ({
          ...prev,
          nombre: exact.nombre || prev.nombre,
          apellido: exact.apellido || prev.apellido,
          edad: exact.fecha_nacimiento ? calcAge(exact.fecha_nacimiento) : prev.edad
        }))
      } else {
        setDniStatus('not_found')
        setFoundPerson(null)
        setFieldsLocked(false)
      }
    } catch {
      setDniStatus('error')
      setFoundPerson(null)
      setFieldsLocked(false)
    }
  }, [])

  const calcAge = (fechaNac) => {
    if (!fechaNac) return ''
    const birth = new Date(fechaNac)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--
    return age > 0 && age < 130 ? String(age) : ''
  }

  const handleDniChange = (value) => {
    const clean = value.replace(/\D/g, '').slice(0, 8)
    setFormData(prev => ({ ...prev, dni: clean }))

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!isEditing) {
      debounceRef.current = setTimeout(() => searchByDni(clean), 400)
    }
  }

  const handleUnlockFields = () => {
    setFieldsLocked(false)
    setDniStatus('not_found')
    setFoundPerson(null)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    const cleanDni = String(formData.dni || '').trim()
    if (!cleanDni || cleanDni.length < 7) return
    if (!formData.nombre.trim() || !formData.apellido.trim()) return
    onSubmit(formData)
  }

  const canSubmit = formData.nombre.trim() && formData.apellido.trim() && String(formData.dni || '').trim().length >= 7

  return (
    <div className="space-y-5">
      {/* ── PASO 1: DNI (campo principal) ── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-black">1</div>
          <label className="text-sm font-bold text-slate-700">
            DNI del familiar <span className="text-red-500">*</span>
          </label>
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            inputMode="numeric"
            value={formData.dni}
            onChange={(e) => handleDniChange(e.target.value)}
            className={`block w-full rounded-2xl border-2 py-4 pl-12 pr-4 text-lg font-mono font-bold tracking-wider transition-all focus:outline-none focus:ring-2 ${
              dniStatus === 'found' ? 'border-emerald-300 bg-emerald-50/50 text-emerald-800 focus:ring-emerald-400' :
              dniStatus === 'not_found' ? 'border-amber-300 bg-amber-50/30 text-slate-800 focus:ring-amber-400' :
              'border-slate-200 bg-white text-slate-800 focus:ring-emerald-400 focus:border-emerald-400'
            }`}
            placeholder="Ingrese el DNI para buscar..."
            maxLength={8}
            autoFocus={!isEditing}
          />
          {/* Status indicator */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-4">
            {dniStatus === 'searching' && (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            )}
            {dniStatus === 'found' && (
              <svg className="h-6 w-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {dniStatus === 'not_found' && formData.dni.length >= 7 && (
              <svg className="h-6 w-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
        </div>

        {/* Status message */}
        {dniStatus === 'found' && foundPerson && (
          <div className="mt-3 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-black text-emerald-800 uppercase">
                    {foundPerson.apellido}, {foundPerson.nombre}
                  </p>
                  <p className="text-xs text-emerald-600 font-medium mt-0.5">
                    Persona encontrada en el sistema — datos autocompletados
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleUnlockFields}
                className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 hover:text-emerald-800 px-2 py-1 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                Editar datos
              </button>
            </div>
          </div>
        )}

        {/* Ai Insights Alert */}
        {dniStatus === 'found' && foundPerson?.ai_insights?.alerta_asistencia_familiar && (
            <div className="mt-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-4 animate-in slide-in-from-top-2 duration-300">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-black text-amber-800 uppercase tracking-wide">Alerta Inteligente (IA)</h4>
                  <p className="text-xs text-amber-700/90 font-medium mb-2 mt-0.5">{foundPerson.ai_insights.mensaje_alerta}</p>
                  <div className="space-y-1.5">
                    {foundPerson.ai_insights.miembros_inferidos.map((m, idx) => (
                      m.asistencias_recientes.length > 0 && (
                        <div key={idx} className="text-[11px] bg-white/50 rounded-lg px-2.5 py-1.5 text-amber-900 border border-amber-500/20 shadow-sm">
                          <span className="font-bold">{m.nombre_completo}</span> <span className="opacity-75">({m.relacion_inferida}) recibió:</span>
                          <ul className="list-disc list-inside mt-1 pl-1">
                            {m.asistencias_recientes.map((a, i) => (
                              <li key={i} className="font-semibold text-amber-700">{a.tipo.replace(/_/g, ' ').toUpperCase()} <span className="font-normal opacity-80 text-amber-900 ml-1">el {a.fecha}</span></li>
                            ))}
                          </ul>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </div>
        )}
        {dniStatus === 'not_found' && formData.dni.length >= 7 && (
          <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs font-bold text-amber-700">
                DNI no registrado en el sistema. Complete los datos manualmente.
              </p>
            </div>
          </div>
        )}
        {!isEditing && formData.dni.length < 7 && formData.dni.length > 0 && (
          <p className="mt-2 text-xs text-slate-400 font-medium pl-1">
            Ingrese al menos 7 dígitos para buscar en la base de datos...
          </p>
        )}
      </div>

      {/* ── PASO 2: Datos personales (aparecen después del DNI) ── */}
      {(formData.dni.length >= 7 || isEditing) && (
        <div className="animate-in slide-in-from-bottom-3 duration-300">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-slate-600 text-white flex items-center justify-center text-xs font-black">2</div>
            <label className="text-sm font-bold text-slate-700">Datos personales</label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50/50">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 ${fieldsLocked ? 'text-emerald-700 bg-emerald-50/50' : 'text-slate-800'}`}
                placeholder="Ej: Juan"
                readOnly={fieldsLocked}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Apellido <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.apellido}
                onChange={(e) => handleChange('apellido', e.target.value)}
                className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 ${fieldsLocked ? 'text-emerald-700 bg-emerald-50/50' : 'text-slate-800'}`}
                placeholder="Ej: Perez"
                readOnly={fieldsLocked}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Edad</label>
              <input
                type="number"
                value={formData.edad}
                onChange={(e) => handleChange('edad', e.target.value)}
                className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 ${fieldsLocked && formData.edad ? 'text-emerald-700 bg-emerald-50/50' : 'text-slate-800'}`}
                placeholder="Ej: 45"
                min="0"
                max="130"
                readOnly={fieldsLocked && Boolean(formData.edad)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── PASO 3: Vínculo familiar ── */}
      {(formData.dni.length >= 7 || isEditing) && (
        <div className="animate-in slide-in-from-bottom-3 duration-300">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center text-xs font-black">3</div>
            <label className="text-sm font-bold text-slate-700">Vínculo y convivencia</label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50/50">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Relación <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.relacion}
                onChange={(e) => handleChange('relacion', e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
              >
                <optgroup label="Padres">
                  <option value="padre">Padre</option>
                  <option value="madre">Madre</option>
                </optgroup>
                <optgroup label="Hijos">
                  <option value="hijo">Hijo</option>
                  <option value="hija">Hija</option>
                </optgroup>
                <optgroup label="Pareja">
                  <option value="esposo">Esposo</option>
                  <option value="esposa">Esposa</option>
                  <option value="concubino">Concubino/a</option>
                </optgroup>
                <optgroup label="Hermanos">
                  <option value="hermano">Hermano</option>
                  <option value="hermana">Hermana</option>
                </optgroup>
                <optgroup label="Abuelos">
                  <option value="abuelo">Abuelo</option>
                  <option value="abuela">Abuela</option>
                </optgroup>
                <optgroup label="Otros">
                  <option value="tio">Tío</option>
                  <option value="tia">Tía</option>
                  <option value="nieto">Nieto</option>
                  <option value="nieta">Nieta</option>
                  <option value="primo">Primo</option>
                  <option value="prima">Prima</option>
                  <option value="cuñado">Cuñado</option>
                  <option value="cuñada">Cuñada</option>
                  <option value="suegro">Suegro</option>
                  <option value="suegra">Suegra</option>
                  <option value="otro">Otro</option>
                </optgroup>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Convive</label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => handleChange('convive', true)}
                  className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all border-2 ${
                    formData.convive
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'
                  }`}
                >
                  ✓ Sí
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('convive', false)}
                  className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all border-2 ${
                    !formData.convive
                      ? 'border-red-400 bg-red-50 text-red-600 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'
                  }`}
                >
                  ✗ No
                </button>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Observaciones</label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => handleChange('observaciones', e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all resize-none min-h-[70px]"
                placeholder="Información adicional sobre el familiar..."
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Botones ── */}
      <div className="flex flex-col sm:flex-row gap-3 pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="flex-1 px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 hover:from-emerald-500 hover:to-teal-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
        >
          {dniStatus === 'found' && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )}
          {isEditing ? 'Actualizar Familiar' : (dniStatus === 'found' ? 'Vincular al Grupo' : 'Agregar Familiar')}
        </button>
      </div>
    </div>
  )
}

export default FamilyMemberForm
